import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Navigation from "../components/Navigation";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { FileText, TrendingUp, Target, MessageSquare, Send, Sparkles, Lightbulb, CheckCircle, Plus, Loader2, Brain, Mic, MicOff } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const menuItems = [
  { icon: FileText, label: "My Training Plan", path: "/plans" },
  { icon: TrendingUp, label: "Mental Performance", path: "/performance" },
  { icon: Target, label: "Goals", path: "/goals" },
  { icon: MessageSquare, label: "Messages", path: "/messages" },
  { icon: MessageSquare, label: "AI Coach", path: "/ai-coach" }
];

export default function AICoach({ user, onLogout }) {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [goalSuggestions, setGoalSuggestions] = useState([]);
  const [basedOnPlans, setBasedOnPlans] = useState([]);
  const [isLoadingGoals, setIsLoadingGoals] = useState(false);
  const [creatingGoalId, setCreatingGoalId] = useState(null);
  const [showGoalPanel, setShowGoalPanel] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);
  const navigate = useNavigate();

  // Voice dictation
  const startListening = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      toast.error("Voice input is not supported in your browser");
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onresult = (event) => {
      let transcript = '';
      for (let i = 0; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      setInputMessage(transcript);
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setIsListening(false);
      if (event.error === 'not-allowed') toast.error("Microphone access denied");
    };

    recognition.onend = () => { setIsListening(false); };

    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  };

  const stopListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setIsListening(false);
  };

  const toggleListening = () => {
    if (isListening) stopListening();
    else startListening();
  };

  useEffect(() => {
    // Welcome message
    setMessages([
      {
        id: 1,
        type: "bot",
        content: "Hello! I'm your AI mental performance coach. I'm here to help you develop mental toughness, improve focus, and optimize your athletic mindset. What would you like to work on today?\n\n💡 Tip: Click 'Suggest Goals from My Plans' to get personalized goal recommendations based on your training plans!",
        timestamp: new Date()
      }
    ]);
  }, []);

  const sendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMessage = {
      id: messages.length + 1,
      type: "user",
      content: inputMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage("");
    setIsLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/ai/chat`, {
        message: inputMessage,
        user_context: {
          name: user.name,
          role: user.role
        }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const botMessage = {
        id: messages.length + 2,
        type: "bot",
        content: response.data.response,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      toast.error("Failed to get AI response");
      const errorMessage = {
        id: messages.length + 2,
        type: "bot",
        content: "I apologize, but I'm having trouble responding right now. Please try asking about specific mental training topics like visualization, confidence building, or handling pressure situations.",
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const suggestGoalsFromPlans = async () => {
    setIsLoadingGoals(true);
    setShowGoalPanel(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/ai/suggest-goals-from-plans`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setGoalSuggestions(response.data.suggestions || []);
      setBasedOnPlans(response.data.based_on_plans || []);
      
      if (response.data.suggestions?.length > 0) {
        toast.success(`Generated ${response.data.suggestions.length} goal suggestions!`);
      } else {
        toast.info(response.data.message || "No suggestions available. Create a training plan first.");
      }
    } catch (error) {
      console.error("Failed to get goal suggestions:", error);
      toast.error("Failed to generate goal suggestions");
    } finally {
      setIsLoadingGoals(false);
    }
  };

  const createGoalFromSuggestion = async (suggestion, index) => {
    setCreatingGoalId(index);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/ai/create-goal-from-suggestion`, suggestion, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`Goal "${suggestion.title}" created!`);
      
      // Remove the created suggestion from the list
      setGoalSuggestions(prev => prev.filter((_, i) => i !== index));
      
      // Add confirmation message to chat
      const confirmMessage = {
        id: messages.length + 1,
        type: "bot",
        content: `✅ I've created a new goal for you: "${suggestion.title}"\n\nCategory: ${suggestion.category}\nPriority: ${suggestion.priority}\nTimeline: ${suggestion.timeline}\n\nYou can view and track this goal in your Goals page.`,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, confirmMessage]);
      
    } catch (error) {
      console.error("Failed to create goal:", error);
      toast.error("Failed to create goal");
    } finally {
      setCreatingGoalId(null);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high': return 'bg-red-100 text-red-700 border-red-200';
      case 'medium': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'low': return 'bg-green-100 text-green-700 border-green-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category?.toLowerCase()) {
      case 'confidence': return '💪';
      case 'focus': return '🎯';
      case 'visualization': return '👁️';
      case 'stress-management': return '🧘';
      case 'mindset': return '🧠';
      case 'pre-performance': return '⚡';
      case 'recovery': return '🔄';
      case 'communication': return '💬';
      default: return '✨';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation user={user} onLogout={onLogout} />

      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        <div className="mb-4 sm:mb-8">
          <h1 data-testid="ai-coach-title" className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 tracking-tight">
            AI Mental Performance Coach
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Personalized insights, training recommendations, and goal suggestions
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-5">
          {/* Main Chat Area */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="p-3 sm:p-5 border-b border-gray-100">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600">
                    <Sparkles className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h2 className="text-sm font-bold text-gray-900">Coach Chat</h2>
                    <p className="text-[10px] sm:text-xs text-gray-400">Ask about mental training or performance</p>
                  </div>
                </div>
              </div>
              
              <div className="p-5">
                {/* Chat Messages */}
                <div className="h-96 overflow-y-auto mb-5 space-y-3 p-4 bg-gray-50 rounded-xl">
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      data-testid={`chat-message-${message.id}`}
                      className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[80%] px-4 py-3 rounded-xl ${
                          message.type === 'user'
                            ? 'bg-blue-600 text-white'
                            : 'bg-white text-gray-800 shadow-sm border border-gray-100'
                        }`}
                      >
                        <p className="text-sm leading-relaxed whitespace-pre-line">{message.content}</p>
                        <p className={`text-[10px] mt-2 ${
                          message.type === 'user' ? 'text-blue-200' : 'text-gray-400'
                        }`}>
                          {message.timestamp.toLocaleTimeString()}
                        </p>
                      </div>
                    </div>
                  ))}
                  
                  {isLoading && (
                    <div className="flex justify-start">
                      <div className="bg-white text-gray-800 shadow-sm border border-gray-100 px-4 py-3 rounded-xl">
                        <div className="flex items-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                          <span className="text-sm text-gray-500">Coach is thinking...</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Message Input */}
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <Textarea
                      value={inputMessage}
                      onChange={(e) => setInputMessage(e.target.value)}
                      placeholder="Ask your coach anything about mental training..."
                      className="resize-none border-gray-200 rounded-xl focus:ring-blue-500/40 focus:border-blue-400 text-sm"
                      rows={2}
                      data-testid="coach-message-input"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          sendMessage();
                        }
                      }}
                    />
                  </div>
                  <Button
                    onClick={toggleListening}
                    variant={isListening ? "destructive" : "outline"}
                    size="icon"
                    data-testid="voice-dictation-btn"
                    className={`flex-shrink-0 h-10 w-10 rounded-xl transition-all ${
                      isListening
                        ? 'bg-red-500 hover:bg-red-600 text-white animate-pulse shadow-lg shadow-red-500/30'
                        : 'border-gray-200 text-gray-500 hover:text-blue-600 hover:border-blue-300'
                    }`}
                    title={isListening ? "Stop dictation" : "Start voice dictation"}
                  >
                    {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                  </Button>
                  <Button
                    onClick={sendMessage}
                    disabled={!inputMessage.trim() || isLoading}
                    className="bg-blue-600 hover:bg-blue-700 px-5 py-2.5 rounded-xl shadow-sm"
                    data-testid="send-message-btn"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Goal Suggestions Panel */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm sticky top-24 overflow-hidden">
              <div className="p-5 border-b border-gray-100">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500">
                    <Lightbulb className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h2 className="text-sm font-bold text-gray-900">Goal Suggestions</h2>
                    <p className="text-xs text-gray-400">AI-powered recommendations from your plans</p>
                  </div>
                </div>
              </div>
              
              <div className="p-5">
                {/* Suggest Goals Button */}
                <Button
                  onClick={suggestGoalsFromPlans}
                  disabled={isLoadingGoals}
                  className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white text-sm font-semibold shadow-sm rounded-xl"
                  data-testid="suggest-goals-btn"
                >
                  {isLoadingGoals ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Analyzing Plans...
                    </>
                  ) : (
                    <>
                      <Brain className="w-4 h-4 mr-2" />
                      Suggest Goals from My Plans
                    </>
                  )}
                </Button>

                {/* Based on Plans Info */}
                {basedOnPlans.length > 0 && (
                  <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-xs text-blue-700 font-medium mb-1">Based on your plans:</p>
                    <div className="space-y-1">
                      {basedOnPlans.map((plan, i) => (
                        <p key={i} className="text-xs text-blue-600 truncate">
                          📋 {plan.title}
                        </p>
                      ))}
                    </div>
                  </div>
                )}

                {/* Goal Suggestions List */}
                {showGoalPanel && (
                  <div className="space-y-3 max-h-[500px] overflow-y-auto">
                    {isLoadingGoals ? (
                      <div className="text-center py-8">
                        <Loader2 className="w-8 h-8 animate-spin text-amber-500 mx-auto mb-2" />
                        <p className="text-sm text-gray-500">Analyzing your training plans...</p>
                      </div>
                    ) : goalSuggestions.length > 0 ? (
                      goalSuggestions.map((suggestion, index) => (
                        <div
                          key={index}
                          className="p-4 bg-gradient-to-br from-gray-50 to-white rounded-lg border border-gray-200 hover:shadow-md transition-shadow"
                          data-testid={`goal-suggestion-${index}`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="text-lg">{getCategoryIcon(suggestion.category)}</span>
                              <h4 className="font-semibold text-gray-900 text-sm leading-tight">
                                {suggestion.title}
                              </h4>
                            </div>
                          </div>
                          
                          <p className="text-xs text-gray-600 mb-3 line-clamp-2">
                            {suggestion.description}
                          </p>
                          
                          <div className="flex flex-wrap gap-2 mb-3">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${getPriorityColor(suggestion.priority)}`}>
                              {suggestion.priority}
                            </span>
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700 border border-purple-200">
                              {suggestion.category}
                            </span>
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200">
                              ⏱️ {suggestion.timeline}
                            </span>
                          </div>
                          
                          <Button
                            onClick={() => createGoalFromSuggestion(suggestion, index)}
                            disabled={creatingGoalId === index}
                            size="sm"
                            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                            data-testid={`create-goal-btn-${index}`}
                          >
                            {creatingGoalId === index ? (
                              <>
                                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                Creating...
                              </>
                            ) : (
                              <>
                                <Plus className="w-3 h-3 mr-1" />
                                Add to My Goals
                              </>
                            )}
                          </Button>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <Target className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                        <p className="text-sm">Click the button above to get AI-powered goal suggestions based on your training plans.</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Quick Link to Goals Page */}
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full rounded-xl text-xs font-semibold"
                    onClick={() => navigate('/goals')}
                    data-testid="view-goals-btn"
                  >
                    <Target className="w-3.5 h-3.5 mr-1.5" />
                    View All My Goals
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mt-8">
          {[
            { label: "Training Plans", desc: "View and create personalized mental training plans", icon: FileText, path: "/plans", gradient: "from-emerald-500 to-teal-600" },
            { label: "Performance Analysis", desc: "Analyze your mental performance patterns", icon: TrendingUp, path: "/performance", gradient: "from-blue-500 to-indigo-600" },
            { label: "Goal Setting", desc: "Set and track mental performance goals", icon: Target, path: "/goals", gradient: "from-amber-500 to-orange-600" },
          ].map((action, i) => (
            <div
              key={i}
              className="bg-white rounded-xl border border-gray-100 p-5 hover:shadow-md hover:border-gray-200 transition-all duration-200 cursor-pointer"
              onClick={() => navigate(action.path)}
            >
              <div className="flex items-center gap-3">
                <div className={`p-2.5 rounded-lg bg-gradient-to-br ${action.gradient}`}>
                  <action.icon className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-gray-900">{action.label}</h3>
                  <p className="text-xs text-gray-400 mt-0.5">{action.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
